'use client'

import { Maximize2 } from 'lucide-react'
import * as React from 'react'

import { cn } from '../cn'
import { Button } from './button'
import { SurfaceCloseButton } from './icon-button-close'
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetTitle } from './sheet'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip'

/**
 * RecordSheet — view/edit a single record in a side panel with N lifecycle
 * actions. Three pinned zones: header / scrolling body / footer.
 *
 * Use this when:
 *   - The user is looking at one entity (a Skill, a Tool, an audit record).
 *   - There are multiple lifecycle actions (Save, Publish, Archive,
 *     Unpublish, etc.) — not just a single submit.
 *   - The header needs an actions menu (kebab) alongside the close button.
 *
 * Use `FormSheet` instead when:
 *   - The sheet is a one-shot create/edit form with a single Save button.
 *
 * Layout invariants this primitive guarantees for free:
 *   1. The kebab/header-action and the X close button live in the SAME
 *      flex row of the pinned header — they never overlap.
 *   2. The footer is pinned (border-t, shrink-0); the body scrolls
 *      independently. Long forms don't push action buttons off-screen.
 *   3. A long title truncates instead of pushing the X off-screen
 *      (`truncate` on title + `min-w-0 flex-1` on its wrapper).
 *
 * See `packages/ui/plans/plan-record-sheet.md` for the design rationale,
 * including the industry convention scan that informed the layout.
 */

type RecordSheetSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full'

interface RecordSheetFullscreenAction {
  onOpen: () => void | Promise<void>
  label?: string
  disabled?: boolean
  loading?: boolean
  hideOnMobile?: boolean
}

interface RecordSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void

  // ── Header ────────────────────────────────────────────────────
  /** Required. Truncates if long. */
  title: string
  /** Optional one-liner under the title. Truncates. */
  description?: React.ReactNode
  /**
   * Metadata row below the title + description — badges, status pills,
   * "Read only" markers. Wraps. Sits below the heading so the title and
   * subtitle land first in the reading order, then the supporting tags.
   */
  meta?: React.ReactNode
  /**
   * Right-side header content rendered LEFT of the X close button.
   * Typically a `<DropdownMenu>` kebab. Pass `null` for none.
   */
  headerAction?: React.ReactNode
  /**
   * Standard affordance for opening this record in its full-page route.
   * Rendered before `headerAction` so bespoke menus stay closest to close.
   */
  fullscreenAction?: RecordSheetFullscreenAction

  // ── Body ──────────────────────────────────────────────────────
  children: React.ReactNode

  // ── Footer ────────────────────────────────────────────────────
  /** Footer content rendered on the LEFT (e.g. destructive Archive). */
  leftFooter?: React.ReactNode
  /**
   * Footer actions rendered on the RIGHT, in DOM order.
   * Last item is visually emphasized as the primary CTA via the caller's
   * own button variant — RecordSheet doesn't reorder or restyle.
   * Pass `null`/`undefined` to omit the entire footer.
   *
   * No "Cancel" button is auto-rendered: the header X is the canonical
   * close affordance, and a redundant Cancel adds noise. If you genuinely
   * need a Cancel — e.g. discarding draft state on a wizard step — wrap
   * a `<SheetClose asChild><Button variant="outline">Cancel</Button></SheetClose>`
   * inside `footerActions`.
   */
  footerActions?: React.ReactNode

  // ── Sizing / behavior ────────────────────────────────────────
  side?: 'right' | 'left'
  size?: RecordSheetSize
  /** Extra classes on the SheetContent — for max-width overrides etc. */
  contentClassName?: string
}

function RecordSheet({
  open,
  onOpenChange,
  title,
  description,
  meta,
  headerAction,
  fullscreenAction,
  children,
  leftFooter,
  footerActions,
  side = 'right',
  size = 'lg',
  contentClassName,
}: RecordSheetProps) {
  const hasFooter = footerActions != null || leftFooter != null

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={side}
        size={size}
        hideCloseButton
        className={cn('flex h-dvh flex-col gap-0 p-0 sm:h-full', contentClassName)}>
        <header className="shrink-0 border-b border-border px-5 pt-4 pb-4">
          {/* Title row: title + headerAction + X share a single
              `items-center` flex so the title text is vertically aligned
              with the icon-button centers, not their tops. Description
              and meta stack below as full-width blocks so the buttons
              don't have to grow to match a tall left column. */}
          <div className="flex items-center gap-2">
            <SheetTitle className="min-w-0 flex-1 truncate text-base font-semibold">{title}</SheetTitle>
            {fullscreenAction ? <RecordSheetFullscreenButton action={fullscreenAction} /> : null}
            {headerAction ? <div className="shrink-0">{headerAction}</div> : null}
            <SheetClose asChild>
              <SurfaceCloseButton className="shrink-0" />
            </SheetClose>
          </div>
          {description ? <SheetDescription className="mt-1 truncate text-xs">{description}</SheetDescription> : null}
          {meta ? <div className="mt-2 flex flex-wrap items-center gap-1.5">{meta}</div> : null}
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

        {hasFooter ? (
          <footer className="flex shrink-0 items-center gap-2 border-t border-border bg-background px-5 py-3">
            {leftFooter ? (
              <div className="mr-auto flex items-center gap-2">{leftFooter}</div>
            ) : (
              <div className="mr-auto" />
            )}
            {footerActions}
          </footer>
        ) : null}
      </SheetContent>
    </Sheet>
  )
}

/**
 * The standard expand-to-full-page affordance. Exported so sheets built on
 * other shells (e.g. `FormSheet`'s `headerAction`) reuse the same control
 * instead of hand-rolling a near-duplicate.
 */
function RecordSheetFullscreenButton({ action }: { action: RecordSheetFullscreenAction }) {
  const label = action.label ?? 'Open full screen'
  const hideOnMobile = action.hideOnMobile ?? true
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={label}
            aria-busy={action.loading ? true : undefined}
            disabled={action.disabled || action.loading}
            onClick={() => void action.onOpen()}
            className={cn('shrink-0', hideOnMobile && 'hidden md:inline-flex')}>
            <Maximize2 className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

type RecordSheetHandoffVersion = string | number

type StoredRecordSheetHandoff<TValue> = {
  version: RecordSheetHandoffVersion
  value: TValue
}

function writeRecordSheetHandoff<TValue>(storageKey: string, version: RecordSheetHandoffVersion, value: TValue): void {
  if (typeof window === 'undefined') {
    return
  }
  try {
    window.sessionStorage.setItem(
      storageKey,
      JSON.stringify({ version, value } satisfies StoredRecordSheetHandoff<TValue>),
    )
  } catch {
    // Storage can be unavailable in private browsing or during SSR hydration.
  }
}

function readRecordSheetHandoff<TValue>(storageKey: string, version: RecordSheetHandoffVersion): TValue | null {
  if (typeof window === 'undefined') {
    return null
  }
  try {
    const raw = window.sessionStorage.getItem(storageKey)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw) as Partial<StoredRecordSheetHandoff<TValue>>
    return parsed.version === version && 'value' in parsed ? (parsed.value as TValue) : null
  } catch {
    return null
  }
}

function clearRecordSheetHandoff(storageKey: string): void {
  if (typeof window === 'undefined') {
    return
  }
  try {
    window.sessionStorage.removeItem(storageKey)
  } catch {
    // Storage can be unavailable in private browsing or during SSR hydration.
  }
}

export {
  clearRecordSheetHandoff,
  readRecordSheetHandoff,
  RecordSheet,
  type RecordSheetFullscreenAction,
  RecordSheetFullscreenButton,
  type RecordSheetProps,
  writeRecordSheetHandoff,
}
